import { Map } from 'immutable';

import createTypedReducer from '../createTypedReducer';
import * as deviceListActions from './action';
import { CHANGE_CLIENT, CLEAR } from '../authentication/action';
import { deviceListItemKey, type DeviceListFilterCounts, type DeviceListItem, type DeviceListSummary } from 'src/service/api/deviceList';

/* Values are plain JS, set with .set() only: the table reads `items` as is. */
export const initialState = Map<string, any>({
  items: [] as DeviceListItem[],
  nextOffset: 0,
  hasMore: false,
  deviceCount: null as number | null,
  summary: null as DeviceListSummary | null,
  loading: false,
  error: null,
  query: null,
  filterCounts: null as DeviceListFilterCounts | null,
});

const reset = () => initialState;

/* Appends a page, skipping rows already shown: rows can shift between offset
 * fetches and would otherwise appear twice. Keyed per group, so a grouped list
 * still shows a device under each group it's in. */
function append (items: DeviceListItem[], page: DeviceListItem[]): DeviceListItem[] {
  const seen = new Set<string>();
  const circuitKey = (item: DeviceListItem, id: string) => `${item.group?.id ?? ''}:${id}`;
  items.forEach((item) => {
    seen.add(deviceListItemKey(item));
    item.circuits.forEach((c) => seen.add(circuitKey(item, c.id)));
  });

  const fresh = page
    .filter((item) => !seen.has(deviceListItemKey(item)))
    .map((item) => ({ ...item, circuits: item.circuits.filter((c) => !seen.has(circuitKey(item, c.id))) }));

  return [...items, ...fresh];
}

export const deviceListReducer = createTypedReducer(initialState, {
  [deviceListActions.FETCH_DEVICE_LIST] (state, action) {
    return reset()
      .set('deviceCount', state.get('deviceCount'))
      .set('summary', state.get('summary'))
      .set('filterCounts', state.get('filterCounts'))
      .set('loading', true)
      .set('query', { selectedFilters: action.selectedFilters, sort: action.sort, scope: action.scope, countKeys: action.countKeys });
  },

  [deviceListActions.FETCH_DEVICE_LIST_NEXT_PAGE] (state) {
    return state.set('loading', true);
  },

  [deviceListActions.FETCH_DEVICE_LIST_SUCCESS] (state, action) {
    return state.withMutations((nextState) =>
      nextState
        .set('items', append(state.get('items'), action.page.data))
        .set('nextOffset', action.page.nextOffset)
        .set('hasMore', action.page.hasMore)
        .set('deviceCount', action.page.offset === 0 ? action.page.deviceCount : state.get('deviceCount'))
        .set('summary', action.page.offset === 0 ? action.page.summary : state.get('summary'))
        .set('filterCounts', action.page.filterCounts ?? state.get('filterCounts'))
        .set('loading', false)
    );
  },

  [deviceListActions.FETCH_DEVICE_LIST_FAILED] (state, action) {
    return state.set('loading', false).set('error', action.err);
  },

  [CHANGE_CLIENT]: reset,
  [CLEAR]: reset,
});
